import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Config from '@src/utils/api/config';
import { usePartnerDropdownContext } from '@src/context/PartnerDropdown';
import numeral from 'numeral';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAnalyticsService } from './hooks';

const GA_HIDDEN_PARTNERS = ['gamezone', 'palmsbet', 'gambly'];

const GA_SCOPE = 'https://www.googleapis.com/auth/analytics.readonly';

const DATE_RANGES = [
  { label: 'Last 7 days', startDate: '7daysAgo', endDate: 'today' },
  { label: 'Last 30 days', startDate: '30daysAgo', endDate: 'today' },
  { label: 'Last 90 days', startDate: '90daysAgo', endDate: 'today' },
];

const StatCard = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Card sx={{ flex: 1, minWidth: { xs: '45%', sm: 'unset' }, borderRadius: '16px' }}>
    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
      <Typography fontSize={{ xs: 10, sm: 12 }} color="#6B7280" fontWeight={500}>{label}</Typography>
      <Typography fontSize={{ xs: 18, sm: 22 }} fontWeight={700} color={color ?? 'text.primary'} mt={0.5}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const TableLoader = () => (
  <TableRow>
    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
      <CircularProgress size={28} />
    </TableCell>
  </TableRow>
);

const EmptyRow = ({ colSpan }: { colSpan: number }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 3, color: '#9CA3AF' }}>
      No data
    </TableCell>
  </TableRow>
);

const AnalyticsBody = () => {
  const { partner } = usePartnerDropdownContext();
  const gaConfig = Config.GA_PER_PARTNER[partner] ?? { propertyId: Config.GA_PROPERTY_ID || '', clientId: Config.GA_CLIENT_ID || '' };

  const [token, setToken] = useState<string | null>(null);
  const [rangeIndex, setRangeIndex] = useState(0);
  const tokenClientRef = useRef<any>(null);
  const queryClient = useQueryClient();
  const dateRange = DATE_RANGES[rangeIndex];

  // Reset token and queries when partner changes
  useEffect(() => {
    setToken(null);
    tokenClientRef.current = null;
    queryClient.removeQueries({ queryKey: ['ga-summary'] });
    queryClient.removeQueries({ queryKey: ['ga-top-pages'] });
    queryClient.removeQueries({ queryKey: ['ga-by-country'] });
    queryClient.removeQueries({ queryKey: ['ga-by-device'] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['ga-summary'] });
    queryClient.invalidateQueries({ queryKey: ['ga-top-pages'] });
    queryClient.invalidateQueries({ queryKey: ['ga-by-country'] });
    queryClient.invalidateQueries({ queryKey: ['ga-by-device'] });
  };

  const { summary, summaryLoading, topPages, pagesLoading, byCountry, countryLoading, byDevice, deviceLoading } =
    useAnalyticsService(token, dateRange, gaConfig.propertyId);

  const requestToken = useCallback((silent = false) => {
    const google = (window as any).google;
    if (!google || !gaConfig.clientId) return;

    if (!tokenClientRef.current) {
      tokenClientRef.current = google.accounts.oauth2.initTokenClient({
        client_id: gaConfig.clientId,
        scope: GA_SCOPE,
        callback: (resp: any) => {
          if (resp.access_token) setToken(resp.access_token);
        },
      });
    }

    tokenClientRef.current.requestAccessToken({ prompt: silent ? '' : 'consent' });
  }, [gaConfig.clientId]);

  useEffect(() => {
    const scriptId = 'gsi-script';
    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const fmt = (v?: string) => numeral(Number(v ?? 0)).format('0,0');
  const fmtPct = (v?: string) => `${(Number(v ?? 0) * 100).toFixed(1)}%`;
  const fmtDur = (v?: string) => {
    const s = Math.round(Number(v ?? 0));
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  if (GA_HIDDEN_PARTNERS.includes(partner)) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h4" fontWeight={700} mb={3}>Analytics</Typography>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={12} gap={1}>
          <Typography fontSize={16} fontWeight={600} color="#9CA3AF">
            Analytics is not available for this partner.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!gaConfig.propertyId || !gaConfig.clientId) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h4" fontWeight={700} mb={3}>Analytics</Typography>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={12} gap={1}>
          <Typography fontSize={16} fontWeight={600} color="#9CA3AF">
            Google Analytics is not configured for this partner.
          </Typography>
          <Typography fontSize={13} color="#9CA3AF">
            Add the partner's GA property ID and client ID to your .env file.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={1.5} mb={3}>
        <Typography fontSize={{ xs: 20, sm: 24 }} fontWeight={700}>Analytics</Typography>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          {token && (
            <Select
              size="small"
              value={rangeIndex}
              onChange={(e) => setRangeIndex(Number(e.target.value))}
              sx={{ minWidth: 130, fontSize: 13, bgcolor: 'white', borderRadius: '10px' }}
            >
              {DATE_RANGES.map((r, i) => (
                <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>{r.label}</MenuItem>
              ))}
            </Select>
          )}
          {token && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: 13 }}
            >
              Refresh
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={() => requestToken(false)}
            sx={{
              bgcolor: token ? '#059669' : '#0C3ACA',
              '&:hover': { bgcolor: token ? '#047857' : '#0930a8' },
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {token ? '✓ Connected' : 'Connect Google Analytics'}
          </Button>
        </Stack>
      </Stack>

      {!token ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={12} gap={2}>
          <CircularProgress size={32} />
          <Typography fontSize={15} color="#9CA3AF">Waiting for Google authorization…</Typography>
        </Box>
      ) : (
        <>
          {summaryLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <Stack direction="row" gap={1.5} mb={3} flexWrap="wrap">
              <StatCard label="Active Users" value={fmt(summary?.activeUsers)} color="#0C3ACA" />
              <StatCard label="Sessions" value={fmt(summary?.sessions)} color="#7C3AED" />
              <StatCard label="Page Views" value={fmt(summary?.pageViews)} color="#059669" />
              <StatCard label="Bounce Rate" value={fmtPct(summary?.bounceRate)} color="#DC2626" />
              <StatCard label="Avg. Duration" value={fmtDur(summary?.avgSessionDuration)} color="#D97706" />
            </Stack>
          )}

          <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} mb={2}>
            <Card sx={{ flex: 2, borderRadius: '16px' }}>
              <CardHeader title="Top Pages" titleTypographyProps={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 700 }} sx={{ pb: 0.5 }} />
              <Divider />
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Page Path</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Title</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Views</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Sessions</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Users</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagesLoading ? <TableLoader /> : topPages.length === 0 ? <EmptyRow colSpan={5} /> : (
                      topPages.map((row: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{row.pagePath}</TableCell>
                          <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{row.pageTitle}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.pageViews)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.sessions)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.activeUsers)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            <Card sx={{ flex: 1, borderRadius: '16px' }}>
              <CardHeader title="Users by Country" titleTypographyProps={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 700 }} sx={{ pb: 0.5 }} />
              <Divider />
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 280 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Country</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Users</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Sessions</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>New</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {countryLoading ? <TableLoader /> : byCountry.length === 0 ? <EmptyRow colSpan={4} /> : (
                      byCountry.map((row: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontSize: 11 }}>{row.country}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.activeUsers)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.sessions)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.newUsers)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Stack>

          <Card sx={{ borderRadius: '16px' }}>
            <CardHeader title="Traffic by Device" titleTypographyProps={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 700 }} sx={{ pb: 0.5 }} />
            <Divider />
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 300 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Device</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Sessions</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Users</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Page Views</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deviceLoading ? <TableLoader /> : byDevice.length === 0 ? <EmptyRow colSpan={4} /> : (
                    byDevice.map((row: any, i: number) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ textTransform: 'capitalize', fontSize: 11 }}>{row.deviceCategory}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.sessions)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.activeUsers)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(row.pageViews)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}
    </Container>
  );
};

export default AnalyticsBody;
